import { useState, useEffect, useCallback } from 'react';
import { UserPlus, RotateCcw, UserX, UserCheck, Eye, Edit2, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { OwnerLayout } from '../../components/OwnerLayout';
import api from '../../api';

function getInitials(name) {
  return name?.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2) || 'US';
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

function Toast({ msg, show }) {
  return <div className={`au-toast ${show ? 'show' : ''}`} style={{ bottom: '24px' }}>{msg}</div>;
}

const ROLE_LABELS = {
  agent: 'Field Agent',
  manager: 'Manager',
  admin: 'Admin',
};

export default function OwnerTeam() {
  const navigate = useNavigate();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('au_user') || '{}'); } catch { return {}; } })();
  const isOwner = currentUser.role === 'owner';
  const isAdmin = currentUser.role === 'admin';
  const isManager = currentUser.role === 'manager';
  const canResetPin = isOwner || isAdmin;
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(null);
  const [newUser, setNewUser] = useState({
    name: '', employeeId: '', pin: '', role: 'agent', branchId: '', scopeType: 'global', recoveryEnabled: true,
  });
  const [newBranch, setNewBranch] = useState({ branchCode: '', branchName: '', region: '' });
  const [resetPin, setResetPin] = useState('');
  const [editUser, setEditUser] = useState({
    name: '', employeeId: '', role: 'agent', branchId: '', scopeType: 'global', recoveryEnabled: true,
  });
  const [toast, setToast] = useState({ show: false, msg: '' });

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: '' }), 2500);
  };

  const loadData = useCallback(async () => {
    try {
      const [usersRes, branchesRes] = await Promise.all([
        api.get('/users/access'),
        api.get('/branches'),
      ]);
      setUsers(usersRes.data || []);
      setBranches(branchesRes.data || []);
    } catch (err) {
      console.error(err);
      showToast('Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const roleNeedsBranch = (role, scopeType) => role !== 'admin' || scopeType === 'branch';

  const handleAddUser = async () => {
    if (!newUser.name.trim() || !newUser.employeeId.trim() || !newUser.pin.trim()) {
      showToast('All fields are required');
      return;
    }
    if (roleNeedsBranch(newUser.role, newUser.scopeType) && !newUser.branchId) {
      showToast('Please select a branch');
      return;
    }
    if (!/^\d{4,6}$/.test(newUser.pin)) {
      showToast('PIN must be 4-6 digits');
      return;
    }

    try {
      const payload = {
        name: newUser.name,
        employeeId: newUser.employeeId,
        pin: newUser.pin,
        role: newUser.role,
        branchId: roleNeedsBranch(newUser.role, newUser.scopeType) ? newUser.branchId : null,
        scopeType: newUser.role === 'admin' ? newUser.scopeType : null,
        recoveryEnabled: newUser.recoveryEnabled,
      };
      await api.post('/users', payload);
      await loadData();
      setNewUser({ name: '', employeeId: '', pin: '', role: 'agent', branchId: '', scopeType: 'global', recoveryEnabled: true });
      setShowAddModal(false);
      showToast('User created successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleAddBranch = async () => {
    if (!newBranch.branchCode.trim() || !newBranch.branchName.trim()) {
      showToast('Branch code and name are required');
      return;
    }
    try {
      const res = await api.post('/branches', newBranch);
      setBranches((prev) => [...prev, res.data]);
      setNewBranch({ branchCode: '', branchName: '', region: '' });
      setShowBranchModal(false);
      showToast('Branch created successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create branch');
    }
  };

  const handleResetPIN = async () => {
    if (!resetPin || !/^\d{4,6}$/.test(resetPin)) {
      showToast('PIN must be 4-6 digits');
      return;
    }
    try {
      await api.patch(`/auth/reset-pin/${showResetModal.id}`, { newPin: resetPin });
      showToast('PIN reset successfully. User will be prompted to change on next login.');
      setShowResetModal(null);
      setResetPin('');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to reset PIN');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.isActive) {
        await api.patch(`/users/${user.id}/deactivate`);
      } else {
        await api.patch(`/users/${user.id}/activate`);
      }
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, isActive: !u.isActive } : u)));
      showToast(`User ${user.isActive ? 'deactivated' : 'activated'}`);
    } catch {
      showToast('Failed to update user status');
    }
  };

  const handleEditClick = (user) => {
    setEditUser({
      name: user.name,
      employeeId: user.employeeId,
      role: user.role || 'agent',
      branchId: user.branchId || '',
      scopeType: user.scopeType || 'global',
      recoveryEnabled: user.recoveryEnabled !== false,
    });
    setShowEditModal(user);
  };

  const handleSaveEdit = async () => {
    if (!editUser.name.trim() || !editUser.employeeId.trim()) {
      showToast('All fields are required');
      return;
    }
    if (roleNeedsBranch(editUser.role, editUser.scopeType) && !editUser.branchId) {
      showToast('Please select a branch');
      return;
    }
    try {
      await api.patch(`/users/${showEditModal.id}`, {
        name: editUser.name,
        employeeId: editUser.employeeId,
      });
      await api.patch(`/users/${showEditModal.id}/access`, {
        role: editUser.role,
        branchId: roleNeedsBranch(editUser.role, editUser.scopeType) ? editUser.branchId : null,
        scopeType: editUser.role === 'admin' ? editUser.scopeType : null,
        recoveryEnabled: editUser.recoveryEnabled,
      });
      await loadData();
      setShowEditModal(null);
      showToast('User updated successfully');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleToggleBranch = async (branch) => {
    try {
      await api.patch(`/branches/${branch.id}/toggle`);
      setBranches((prev) => prev.map((b) => (b.id === branch.id ? { ...b, isActive: !b.isActive } : b)));
      showToast(`Branch ${branch.isActive ? 'deactivated' : 'activated'}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update branch');
    }
  };

  const activeUsers = users.filter((u) => u.isActive);
  const inactiveUsers = users.filter((u) => !u.isActive);

  return (
    <OwnerLayout title="Team">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading font-bold text-navy text-lg">Team & Access Management</h2>
          <p className="text-sm text-gray">{activeUsers.length} active · {inactiveUsers.length} inactive · {branches.length} branches</p>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <>
              <button className="btn-outline" onClick={() => setShowBranchModal(true)} data-testid="add-branch-btn">
                <Building2 size={14} style={{ marginRight: '4px' }} /> Add Branch
              </button>
              <button className="btn-navy" onClick={() => setShowAddModal(true)} data-testid="add-user-btn">
                <UserPlus size={14} style={{ marginRight: '4px' }} /> Add User
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray">Loading…</div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {users.map((user) => (
              <div key={user.id} className="agent-card fu" data-testid={`user-${user.id}`}>
                <div className={`agent-card-bar ${user.agentStatus || 'inactive'}`} />
                <div className="agent-card-body">
                  <div className="agent-card-top">
                    <div className="agent-av">
                      {getInitials(user.name)}
                      <span className={`agent-status-dot ${user.agentStatus || 'inactive'}`} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="agent-name">{user.name}</div>
                      <div className="text-xs text-gray">{user.employeeId}</div>
                      <div className="text-xs text-gray">{ROLE_LABELS[user.role] || user.role}</div>
                      <div className="text-xs text-gray">{user.branchName || (user.scopeType === 'global' ? 'Global scope' : 'No branch')}</div>
                      <div className={`agent-last-active ${user.agentStatus === 'active' ? 'active-color' : user.agentStatus === 'week' ? 'week-color' : ''}`}>
                        {timeAgo(user.lastActiveAt)}
                      </div>
                    </div>
                    {!user.isActive && <span className="status-pill off">Deactivated</span>}
                  </div>
                  <div className="agent-stats-grid">
                    <div className="agent-stat">
                      <div className="agent-stat-label">Merchants</div>
                      <div className="agent-stat-val">{user.merchantCount || 0}</div>
                    </div>
                    <div className="agent-stat">
                      <div className="agent-stat-label">UPI/QR</div>
                      <div className="agent-stat-val red">{user.upiQrCount || 0}</div>
                    </div>
                  </div>
                </div>
                <div className="agent-card-actions">
                  {isOwner && (
                    <button className="agent-view-link" onClick={() => handleEditClick(user)} data-testid={`edit-user-${user.id}`}>
                      <Edit2 size={12} style={{ marginRight: '4px' }} /> Edit
                    </button>
                  )}
                  {user.role === 'agent' && (
                    <button
                      className="agent-view-link"
                      onClick={() => navigate(`/owner/merchants?agentId=${user.id}`)}
                      data-testid={`view-merchants-${user.id}`}
                    >
                      <Eye size={12} style={{ marginRight: '4px' }} /> View Merchants
                    </button>
                  )}
                  {canResetPin && user.role !== 'owner' && !(isAdmin && user.role === 'admin') && (
                    <button
                      className="agent-view-link"
                      onClick={() => {
                        setShowResetModal(user);
                        setResetPin('');
                      }}
                      data-testid={`reset-pin-${user.id}`}
                    >
                      <RotateCcw size={12} style={{ marginRight: '4px' }} /> Reset PIN
                    </button>
                  )}
                  {isOwner && (
                    <button
                      className={`agent-view-link ${user.isActive ? 'text-red' : 'text-green-600'}`}
                      onClick={() => handleToggleActive(user)}
                      data-testid={`toggle-active-${user.id}`}
                    >
                      {user.isActive ? (
                        <><UserX size={12} style={{ marginRight: '4px' }} /> Deactivate</>
                      ) : (
                        <><UserCheck size={12} style={{ marginRight: '4px' }} /> Activate</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="card p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-navy font-semibold">Branches</h3>
              <span className="text-xs text-gray">Owner managed</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {branches.map((branch) => (
                <div key={branch.id} className="border rounded-lg p-3 bg-white">
                  <div className="font-semibold text-navy">{branch.branchName}</div>
                  <div className="text-xs text-gray">{branch.branchCode} {branch.region ? `· ${branch.region}` : ''}</div>
                  {isOwner && (
                    <div className="mt-2">
                      <button className={`btn-outline text-xs ${branch.isActive ? 'text-red' : 'text-green-700'}`} onClick={() => handleToggleBranch(branch)}>
                        {branch.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {showAddModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}>
          <div className="owner-modal" style={{ width: '460px' }}>
            <div className="modal-head">
              <div className="modal-icon"><UserPlus size={16} /></div>
              <div>
                <div className="modal-title">Add New User</div>
                <div className="modal-subtitle">Create employee and assign access</div>
              </div>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" placeholder="e.g. Rahul Sharma" value={newUser.name} onChange={(e) => setNewUser((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID</label>
                <input className="form-input" placeholder="e.g. AU-007" value={newUser.employeeId} onChange={(e) => setNewUser((p) => ({ ...p, employeeId: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Initial PIN (4-6 digits)</label>
                <input className="form-input" type="password" placeholder="e.g. 1234" value={newUser.pin} onChange={(e) => setNewUser((p) => ({ ...p, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))} inputMode="numeric" maxLength={6} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-input" value={newUser.role} onChange={(e) => setNewUser((p) => ({ ...p, role: e.target.value }))}>
                  <option value="agent">Field Agent</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {newUser.role === 'admin' && (
                <div className="form-group">
                  <label className="form-label">Admin Scope</label>
                  <select className="form-input" value={newUser.scopeType} onChange={(e) => setNewUser((p) => ({ ...p, scopeType: e.target.value }))}>
                    <option value="global">Global</option>
                    <option value="branch">Branch</option>
                  </select>
                </div>
              )}
              {roleNeedsBranch(newUser.role, newUser.scopeType) && (
                <div className="form-group">
                  <label className="form-label">Branch</label>
                  <select className="form-input" value={newUser.branchId} onChange={(e) => setNewUser((p) => ({ ...p, branchId: e.target.value }))}>
                    <option value="">Select branch</option>
                    {branches.filter((b) => b.isActive).map((b) => (
                      <option key={b.id} value={b.id}>{b.branchName} ({b.branchCode})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <input type="checkbox" checked={newUser.recoveryEnabled} onChange={(e) => setNewUser((p) => ({ ...p, recoveryEnabled: e.target.checked }))} />
                  Recovery Enabled
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={handleAddUser}>Create User</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBranchModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowBranchModal(false); }}>
          <div className="owner-modal" style={{ width: '420px' }}>
            <div className="modal-head">
              <div className="modal-icon"><Building2 size={16} /></div>
              <div>
                <div className="modal-title">Add Branch</div>
                <div className="modal-subtitle">Create a new operational branch</div>
              </div>
              <button className="modal-close" onClick={() => setShowBranchModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Branch Code</label>
                <input className="form-input" placeholder="e.g. MAIN" value={newBranch.branchCode} onChange={(e) => setNewBranch((p) => ({ ...p, branchCode: e.target.value.toUpperCase() }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Branch Name</label>
                <input className="form-input" placeholder="e.g. Main Branch" value={newBranch.branchName} onChange={(e) => setNewBranch((p) => ({ ...p, branchName: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Region (optional)</label>
                <input className="form-input" placeholder="e.g. Amravati" value={newBranch.region} onChange={(e) => setNewBranch((p) => ({ ...p, region: e.target.value }))} />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowBranchModal(false)}>Cancel</button>
                <button className="btn-navy" onClick={handleAddBranch}>Create Branch</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showResetModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowResetModal(null); }}>
          <div className="owner-modal" style={{ width: '380px' }}>
            <div className="modal-head">
              <div className="modal-icon"><RotateCcw size={16} /></div>
              <div>
                <div className="modal-title">Reset PIN</div>
                <div className="modal-subtitle">{showResetModal.name} ({showResetModal.employeeId})</div>
              </div>
              <button className="modal-close" onClick={() => setShowResetModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">New PIN (4-6 digits)</label>
                <input
                  className="form-input"
                  type="password"
                  placeholder="Enter new PIN"
                  value={resetPin}
                  onChange={(e) => setResetPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  maxLength={6}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowResetModal(null)}>Cancel</button>
                <button className="btn-navy" onClick={handleResetPIN}>Reset PIN</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="owner-modal-overlay open" onClick={(e) => { if (e.target === e.currentTarget) setShowEditModal(null); }}>
          <div className="owner-modal" style={{ width: '460px' }}>
            <div className="modal-head">
              <div className="modal-icon"><Edit2 size={16} /></div>
              <div>
                <div className="modal-title">Edit User</div>
                <div className="modal-subtitle">Update profile and access</div>
              </div>
              <button className="modal-close" onClick={() => setShowEditModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={editUser.name} onChange={(e) => setEditUser((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID</label>
                <input className="form-input" value={editUser.employeeId} onChange={(e) => setEditUser((p) => ({ ...p, employeeId: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-input" value={editUser.role} onChange={(e) => setEditUser((p) => ({ ...p, role: e.target.value }))}>
                  <option value="agent">Field Agent</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {editUser.role === 'admin' && (
                <div className="form-group">
                  <label className="form-label">Admin Scope</label>
                  <select className="form-input" value={editUser.scopeType} onChange={(e) => setEditUser((p) => ({ ...p, scopeType: e.target.value }))}>
                    <option value="global">Global</option>
                    <option value="branch">Branch</option>
                  </select>
                </div>
              )}
              {roleNeedsBranch(editUser.role, editUser.scopeType) && (
                <div className="form-group">
                  <label className="form-label">Branch</label>
                  <select className="form-input" value={editUser.branchId} onChange={(e) => setEditUser((p) => ({ ...p, branchId: e.target.value }))}>
                    <option value="">Select branch</option>
                    {branches.filter((b) => b.isActive).map((b) => (
                      <option key={b.id} value={b.id}>{b.branchName} ({b.branchCode})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label className="form-label flex items-center gap-2">
                  <input type="checkbox" checked={editUser.recoveryEnabled} onChange={(e) => setEditUser((p) => ({ ...p, recoveryEnabled: e.target.checked }))} />
                  Recovery Enabled
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button className="btn-outline" onClick={() => setShowEditModal(null)}>Cancel</button>
                <button className="btn-navy" onClick={handleSaveEdit}>Save Changes</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast msg={toast.msg} show={toast.show} />
    </OwnerLayout>
  );
}
